import { type Plan, updateCheckoutSession } from '@otter-ai/recall-client';
import { useCheckout } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

type UpdateCheckoutPlanParams = {
  plan: Plan;
  seatCount: number;
};

export const useUpdateCheckoutPlan = ({
  selectedPlan,
  setSelectedPlan,
  setSessionIsUpdating,
}: {
  selectedPlan: Plan | undefined;
  setSelectedPlan: (plan: Plan) => void;
  setSessionIsUpdating: (isUpdating: boolean) => void;
}) => {
  const { runServerUpdate, id } = useCheckout();

  const updateCheckoutPlanMutation = useMutation<
    Plan | undefined,
    Error,
    UpdateCheckoutPlanParams,
    { plan: Plan; previousPlan: Plan } | undefined
  >({
    onMutate: (variables) => {
      if (!selectedPlan) return;
      return { plan: variables.plan, previousPlan: selectedPlan };
    },
    mutationFn: async ({ plan, seatCount }: UpdateCheckoutPlanParams) => {
      if (!selectedPlan || plan.id === selectedPlan.id) {
        return;
      }
      setSelectedPlan(plan);
      await runServerUpdate(() =>
        updateCheckoutSession({
          body: {
            checkout_session_id: id,
            session_params: {
              line_items: [
                {
                  adjustable_quantity: { enabled: true },
                  price: plan.stripe_plan_id ?? '',
                  quantity: seatCount,
                },
              ],
            },
          },
          throwOnError: true,
        }),
      );

      return plan;
    },
    onError: (_, __, context) => {
      if (context?.previousPlan) {
        setSelectedPlan(context.previousPlan);
      }
    },
  });

  useEffect(() => {
    setSessionIsUpdating(updateCheckoutPlanMutation.isPending);
  }, [updateCheckoutPlanMutation.isPending, setSessionIsUpdating]);

  return updateCheckoutPlanMutation;
};
